import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import documentService from '../../services/documentService';
import DocumentForm from '../../components/Documents/DocumentForm';
import Toast from '../../components/Common/Toast';
import FormLoadingSpinner from '../../components/Common/FormLoadingSpinner';
import IntegrationDocumentBadge from '../../components/Documents/IntegrationDocumentBadge';

const EditDocument = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    loadDocument();
  }, [id]);

  const loadDocument = async () => {
    try {
      setLoading(true);
      const response = await documentService.getDocument(id);
      
      if (response.success && response.data) {
        setDocument(response.data);
      } else {
        setToast({
          isOpen: true,
          message: 'No se pudo cargar el documento',
          type: 'error'
        });
        setTimeout(() => {
          navigate('/documents');
        }, 2000);
      }
    } catch (error) {
      console.error('Error al cargar documento:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar el documento',
        type: 'error'
      });
      setTimeout(() => {
        navigate('/documents');
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = (updatedDocument) => {
    setToast({
      isOpen: true,
      message: 'Documento actualizado exitosamente',
      type: 'success'
    });
    setTimeout(() => {
      navigate('/documents');
    }, 1500);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-sm border border-gray-100">
          <FormLoadingSpinner hint="Obteniendo la información del documento" />
        </div>
      </div>
    );
  }

  if (!document) {
    return null;
  }

  const isIntegrationReadOnly = Boolean(document.is_integration_read_only || document.integration_source);

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">
            {isIntegrationReadOnly ? 'Ver documento integrado' : 'Editar Documento'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isIntegrationReadOnly
              ? 'Documento sincronizado desde un ERP externo (solo consulta)'
              : 'Modifique la información del documento'}
          </p>
        </div>

        {isIntegrationReadOnly && (
          <div className="mb-6 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <IntegrationDocumentBadge
                integrationSourceLabel={document.integration_source_label}
                integrationExternalReference={document.integration_external_reference}
                compact
              />
              <span className="font-medium">
                {document.integration_source_label || 'ERP externo'}
              </span>
            </div>
            <p>
              Ref. externa: <span className="font-mono">{document.integration_external_reference || '—'}</span>.
              Solo consulta en Contify. Para modificar o eliminar, use la API de integración del ERP origen.
            </p>
          </div>
        )}

        {/* Formulario */}
        <DocumentForm
          isOpen={true}
          onClose={() => navigate('/documents')}
          document={document}
          onSuccess={handleSuccess}
          isPageMode={true}
          readOnly={isIntegrationReadOnly}
        />

        {/* Toast de notificaciones */}
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default EditDocument;

